import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import {
  UserGroupIcon,
  CurrencyRupeeIcon,
  ChartBarIcon,
  ChartPieIcon,
  ClockIcon,
  CalendarIcon,
  SunIcon
} from '@heroicons/react/24/outline';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo } from '../utils/deviceInfo';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const API_URL = process.env.REACT_APP_API_URL;

// Skeleton Loader Components
const CardSkeleton = () => (
  <div className="rounded-lg shadow p-6 bg-gray-200 dark:bg-gray-700 animate-pulse">
    <div className="flex justify-between items-start">
      <div className="space-y-2">
        <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded"></div>
        <div className="h-8 w-32 bg-gray-300 dark:bg-gray-600 rounded"></div>
      </div>
      <div className="p-2 rounded-full bg-gray-300 dark:bg-gray-600">
        <div className="h-6 w-6"></div>
      </div>
    </div>
  </div>
);

const ChartSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 h-64 flex items-center justify-center">
    <div className="animate-pulse text-gray-400">Loading chart data...</div>
  </div>
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('daily');
  const [dashboardData, setDashboardData] = useState(null);
  const navigate = useNavigate();
  const [access_data, setAccess_data] = useState([]);

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchDashboardData();
    }
  }, [access_data?.username, timeRange]);

  // Mock API data for different time ranges
  const mockApiResponse = (range) => {
    const now = new Date();
    let labels = [];
    let rechargeData = [];
    let billData = [];
    let userActivity = [];

    // Generate data based on time range
    if (range === 'daily') {
      labels = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (6 - i));
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      });

      rechargeData = labels.map(() => Math.floor(Math.random() * 20000) + 10000);
      billData = labels.map(() => Math.floor(Math.random() * 15000) + 8000);
      userActivity = labels.map(() => Math.floor(Math.random() * 1000) + 500);
    }
    else if (range === 'weekly') {
      labels = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (6 - i) * 7);
        return `Week ${date.getWeek()}`;
      });

      rechargeData = labels.map(() => Math.floor(Math.random() * 50000) + 30000);
      billData = labels.map(() => Math.floor(Math.random() * 40000) + 20000);
      userActivity = labels.map(() => Math.floor(Math.random() * 3000) + 1500);
    }
    else { // monthly
      labels = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(now);
        date.setMonth(date.getMonth() - (6 - i));
        return date.toLocaleDateString('en-US', { month: 'short' });
      });

      rechargeData = labels.map(() => Math.floor(Math.random() * 80000) + 50000);
      billData = labels.map(() => Math.floor(Math.random() * 70000) + 40000);
      userActivity = labels.map(() => Math.floor(Math.random() * 5000) + 3000);
    }

    return {
      status: true,
      data: {
        cards: {
          totalTransactions: Math.floor(Math.random() * 200000) + 100000,
          activeUsers: Math.floor(Math.random() * 5000) + 2000,
          rechargeSuccessRate: (Math.random() * 5 + 95).toFixed(1),
          billPayments: Math.floor(Math.random() * 100000) + 50000
        },
        charts: {
          transactionTrends: {
            labels,
            recharge: rechargeData,
            billPayment: billData
          },
          userActivity: {
            labels,
            data: userActivity
          },
          transactionDistribution: {
            mobileRecharge: Math.floor(Math.random() * 40) + 30,
            electricityBill: Math.floor(Math.random() * 30) + 20,
            dthRecharge: Math.floor(Math.random() * 20) + 10,
            waterBill: Math.floor(Math.random() * 15) + 5,
            gasBill: Math.floor(Math.random() * 10) + 5,
            otherPayments: Math.floor(Math.random() * 10) + 2
          },
          successRate: {
            successful: Math.floor(Math.random() * 10) + 85,
            failed: Math.floor(Math.random() * 5) + 5,
            pending: Math.floor(Math.random() * 5) + 2
          },
          comparisonData: {
            prepaidMobile: Math.floor(Math.random() * 50000) + 30000,
            postpaidMobile: Math.floor(Math.random() * 30000) + 15000,
            electricity: Math.floor(Math.random() * 40000) + 25000,
            dth: Math.floor(Math.random() * 20000) + 10000,
            broadband: Math.floor(Math.random() * 15000) + 8000,
            gas: Math.floor(Math.random() * 12000) + 5000
          }
        }
      }
    };
  };

  // Helper to get week number
  Date.prototype.getWeek = function () {
    const date = new Date(this.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          range: timeRange
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}my-dashboard.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify({
          encrypted_key: encryptedMessage.encryptedKey,
          encrypted_data: encryptedMessage.encryptedData,
        }),
      });

      const result = await response.json();

      if (result.status === true) {
        setDashboardData(result.data);
      } else {
        toast.error(result.message || "Failed to fetch dashboard data.");
      }
    } catch (error) {
      console.error("Fetch dashboard error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data based on fetched data
  const prepareChartData = () => {
    if (!dashboardData) return null;

    // Transaction Trends Data
    const transactionTrendsData = {
      labels: dashboardData.charts.transactionTrends.labels,
      datasets: [
        {
          label: 'Recharge',
          data: dashboardData.charts.transactionTrends.recharge,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: true
        },
        {
          label: 'Bill Payment',
          data: dashboardData.charts.transactionTrends.billPayment,
          borderColor: 'rgba(16, 185, 129, 1)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.3,
          fill: true
        }
      ]
    };

    // User Activity Data
    const userActivityData = {
      labels: dashboardData.charts.userActivity.labels,
      datasets: [
        {
          label: 'Active Users',
          data: dashboardData.charts.userActivity.data,
          backgroundColor: 'rgba(79, 70, 229, 0.7)',
          borderColor: 'rgba(79, 70, 229, 1)',
          borderWidth: 1
        }
      ]
    };

    // Transaction Distribution Data
    const transactionDistributionData = {
      labels: [
        'Add Money',
        'Recharge',
        'Bill Payment',
        'Bonus',
        'Debit Money',
        'Fund Request',
        'Credit from Admin',
        'Debit Adjustment',
        'Transfer',
        'Payout to Bank',
        'Settlement to Wallet',
        'Credit Adjustment',
        'SMS Charges',
        'Others'
      ],
      datasets: [
        {
          data: [
            dashboardData.charts.transactionDistribution.addMoney,
            dashboardData.charts.transactionDistribution.recharge,
            dashboardData.charts.transactionDistribution.billPayment,
            dashboardData.charts.transactionDistribution.bonus,
            dashboardData.charts.transactionDistribution.debitMoney,
            dashboardData.charts.transactionDistribution.fundRequest,
            dashboardData.charts.transactionDistribution.creditFromAdmin,
            dashboardData.charts.transactionDistribution.debitAdjustment,
            dashboardData.charts.transactionDistribution.transfer,
            dashboardData.charts.transactionDistribution.payoutBank,
            dashboardData.charts.transactionDistribution.settlementWallet,
            dashboardData.charts.transactionDistribution.creditAdjustment,
            dashboardData.charts.transactionDistribution.smsCharges,
            dashboardData.charts.transactionDistribution.others
          ],
          backgroundColor: [
            'rgba(59, 130, 246, 0.7)',  // Add Money
            'rgba(16, 185, 129, 0.7)',  // Recharge
            'rgba(168, 85, 247, 0.7)',  // Bill Payment
            'rgba(249, 115, 22, 0.7)',  // Bonus
            'rgba(220, 38, 38, 0.7)',   // Debit Money
            'rgba(234, 179, 8, 0.7)',   // Fund Request
            'rgba(96, 165, 250, 0.7)',  // Credit from Admin
            'rgba(139, 92, 246, 0.7)',  // Debit Adjustment
            'rgba(34, 197, 94, 0.7)',   // Transfer
            'rgba(244, 63, 94, 0.7)',   // Payout to Bank
            'rgba(20, 184, 166, 0.7)',  // Settlement to Wallet
            'rgba(249, 168, 212, 0.7)', // Credit Adjustment
            'rgba(163, 163, 163, 0.7)', // SMS Charges
            'rgba(107, 114, 128, 0.7)'  // Others
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(168, 85, 247, 1)',
            'rgba(249, 115, 22, 1)',
            'rgba(220, 38, 38, 1)',
            'rgba(234, 179, 8, 1)',
            'rgba(96, 165, 250, 1)',
            'rgba(139, 92, 246, 1)',
            'rgba(34, 197, 94, 1)',
            'rgba(244, 63, 94, 1)',
            'rgba(20, 184, 166, 1)',
            'rgba(249, 168, 212, 1)',
            'rgba(163, 163, 163, 1)',
            'rgba(107, 114, 128, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    // Success Rate Data
    const successRateData = {
      labels: ['Successful', 'Failed', 'Pending', 'Initiate'],
      datasets: [
        {
          data: [
            dashboardData.charts.successRate.successful,
            dashboardData.charts.successRate.failed,
            dashboardData.charts.successRate.pending,
            dashboardData.charts.successRate.initiate
          ],
          backgroundColor: [
            'rgba(16, 185, 129, 0.7)',  // Successful
            'rgba(220, 38, 38, 0.7)',   // Failed
            'rgba(234, 179, 8, 0.7)',   // Pending
            'rgba(59, 130, 246, 0.7)'   // Initiate (Blue)
          ],
          borderColor: [
            'rgba(16, 185, 129, 1)',
            'rgba(220, 38, 38, 1)',
            'rgba(234, 179, 8, 1)',
            'rgba(59, 130, 246, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    // Comparison Data
    const comparisonData = {
      labels: ['Credit', 'debit'],
      datasets: [
        {
          label: 'Credit vs Debit',
          data: [
            dashboardData.charts.comparisonData.credit,
            dashboardData.charts.comparisonData.debit,
          ],
          backgroundColor: [
            'rgba(59, 130, 246, 0.7)', // Credit
            'rgba(16, 185, 129, 0.7)'  // Debit
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    return {
      transactionTrendsData,
      userActivityData,
      transactionDistributionData,
      successRateData,
      comparisonData
    };
  };

  const chartData = prepareChartData();

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />

        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {/* Cards Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {loading ? (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              ) : dashboardData ? (
                <>
                  <div className="bg-blue-500 rounded-lg shadow p-6 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-80">Mobile Recharge</p>
                        <h3 className="text-2xl font-bold mt-1">₹{dashboardData.cards.recharge.toLocaleString()}</h3>
                      </div>
                      <div className="p-2 rounded-full bg-white bg-opacity-20">
                        <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-500 rounded-lg shadow p-6 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-80">Bill Payment</p>
                        <h3 className="text-2xl font-bold mt-1">₹{dashboardData.cards.bill.toLocaleString()}</h3>
                      </div>
                      <div className="p-2 rounded-full bg-white bg-opacity-20">
                        <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-500 rounded-lg shadow p-6 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-80">Topup</p>
                        <h3 className="text-2xl font-bold mt-1">₹{dashboardData.cards.topup.toLocaleString()}</h3>
                      </div>
                      <div className="p-2 rounded-full bg-white bg-opacity-20">
                        <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-500 rounded-lg shadow p-6 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-80">User</p>
                        <h3 className="text-2xl font-bold mt-1">{dashboardData.cards.user.toLocaleString()}</h3>
                      </div>
                      <div className="p-2 rounded-full bg-white bg-opacity-20">
                        <UserGroupIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              )}
            </div>

            {/* Time Range Selector */}
            <div className="flex justify-end mb-6">
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => setTimeRange('daily')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-lg flex items-center ${timeRange === 'daily' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}
                >
                  <SunIcon className="h-4 w-4 mr-2" />
                  Daily (7d)
                </button>
                <button
                  onClick={() => setTimeRange('weekly')}
                  className={`px-4 py-2 text-sm font-medium border-l border-r border-gray-200 dark:border-gray-600 flex items-center ${timeRange === 'weekly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}
                >
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Weekly (7w)
                </button>
                <button
                  onClick={() => setTimeRange('monthly')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-lg flex items-center ${timeRange === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300'}`}
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Monthly (7m)
                </button>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Transaction Trends Chart */}
              {loading || !chartData ? (
                <ChartSkeleton />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {timeRange === 'daily' ? 'Daily Transaction Trends (7 days)' :
                        timeRange === 'weekly' ? 'Weekly Transaction Trends (7 weeks)' :
                          'Monthly Transaction Trends (7 months)'}
                    </h3>
                    <ChartBarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="h-64">
                    <Line
                      data={chartData.transactionTrendsData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          tooltip: {
                            mode: 'index',
                            intersect: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                          x: {
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                        },
                        interaction: {
                          mode: 'nearest',
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* User Activity Chart */}
              {loading || !chartData ? (
                <ChartSkeleton />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {timeRange === 'daily' ? 'Daily User Activity (7 days)' :
                        timeRange === 'weekly' ? 'Weekly User Activity (7 weeks)' :
                          'Monthly User Activity (7 months)'}
                    </h3>
                    <UserGroupIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="h-64">
                    <Bar
                      data={chartData.userActivityData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                          x: {
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Second Row of Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Transaction Distribution Chart */}
              {loading || !chartData ? (
                <ChartSkeleton />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Transaction Distribution
                    </h3>
                    <ChartPieIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="h-64">
                    <Pie
                      data={chartData.transactionDistributionData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'right',
                          },
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Success Rate Chart */}
              {loading || !chartData ? (
                <ChartSkeleton />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Transaction Success Rate
                    </h3>
                    <ChartPieIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="h-64">
                    <Doughnut
                      data={chartData.successRateData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'right',
                          },
                        },
                        cutout: '70%'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Recharge vs Bill Payment Chart */}
              {loading || !chartData ? (
                <ChartSkeleton />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Credit VS Debit
                    </h3>
                    <ChartBarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="h-64">
                    <Bar
                      data={chartData.comparisonData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                          x: {
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Dashboard;